import { useEffect, useState } from 'react';
import { ActionsForms } from '../../GenericBaseForms/ActionsForms/ActionsForms';
import { ContainerForms } from '../../GenericBaseForms/ContainerForms/ContainerForms';
import TitleForms from '../../GenericBaseForms/TitleForms/TitleForms';
import LabelForms from '../../GenericBaseForms/LabelForms/LabelForms';
import React from 'react';
import './ProductDetailsFormComponent.scss';
import { getIn, useFormik } from 'formik';
import * as Yup from 'yup';
import TextInputComponent from '../../GenericBaseForms/TextInputComponent/TextInputComponent';
import CheckboxFormComponent from '../../GenericBaseForms/CheckboxFormComponent/CheckboxFormComponent';
import { validationMessages, validationRegex } from '../../../../common/constants';
import {
	atMostMessaege,
	decimalDigitsRegex,
	validateExactLengthMessage,
	validateMessageMax
} from '../../../../helpers/formFieldValidations';
import { IProfileState, ProductInfo } from '../../../../redux/AppReducer';
import { capitalizeString } from '../../../../helpers/genericHelper';
import { formatErrors } from '../../BrandForms/model';
import { DEFAULT_LABELS } from './model';
import LoadingBobComponent from '../../GenericBaseForms/LoadingBobComponent/LoadingBobComponent';
interface IProductDetailsForm {
	productState: ProductInfo;
	onUpdateProfileProduct: (productState: ProductInfo) => void;
	onCancel: () => void;
	isEditing: string | null;
	toggleEditing: (id: string | null) => void;
	onChange: (data: any) => void;
	onValidationChange: (isValid: boolean) => void;
	profileState: IProfileState;
	setErrors: (errors: any) => void;
}
interface IDimensions {
	height: string;
	width: string;
	depth: string;
	weight: string;
}

const threeTwoRegex = decimalDigitsRegex(3, 2);
const threeThree = decimalDigitsRegex(3, 3);
const error_two_decimals = 'Enter a valid number with at most 2 decimals';
const ProductDetailFormSchema = Yup.object().shape({
	flagshipProduct: Yup.boolean().required(validationMessages.field_required),
	dimensions: Yup.object().shape({
		height: Yup.string().matches(threeTwoRegex, atMostMessaege('2 decimals and 3 integers')),
		width: Yup.string().matches(threeTwoRegex, atMostMessaege('2 decimals and 3 integers')),
		depth: Yup.string().matches(threeTwoRegex, atMostMessaege('2 decimals and 3 integers')),
		weight: Yup.string().matches(threeThree, atMostMessaege('3 decimals and 3 integers'))
	}),
	productGTIN: Yup.string().notRequired(),
	productMSRP: Yup.string()
		.notRequired()
		.matches(validationRegex.two_decimal_regex, error_two_decimals),
	launchYear: Yup.number()
		.test(
			'length',
			validateExactLengthMessage('4 digits'),
			val => val !== undefined && val?.toString().length === 4
		)
		.max(new Date().getFullYear(), validateMessageMax(new Date().getFullYear().toString())),
	productShelfLife: Yup.number()
});

const ProductDetailsFormComponent: React.FC<IProductDetailsForm> = ({
	productState,
	onUpdateProfileProduct,
	onCancel,
	isEditing,
	toggleEditing,
	onChange,
	onValidationChange,
	setErrors,
	profileState
}) => {
	const [ID, setID] = useState<string | null>(null);
	const [isVisible, setVisibility] = useState<boolean>(true);

	const toggleVisibility = () => {
		setVisibility(!isVisible);
	};

	const dimensionsProducts: IDimensions = {
		height: '',
		width: '',
		depth: '',
		weight: ''
	};

	const formatDimentionsPlaceholder = (key: string) => {
		return `${capitalizeString(key)} ${key === 'weight' ? '(in ounces)' : '(in inches)'}`;
	};

	const { errors, values, handleChange, handleReset, submitForm, isValid, setValues } = useFormik({
		initialValues: {
			...productState,
			flagshipProduct: productState.flagshipProduct,
			dimensions: {
				height: productState.dimensions.height?.inches
					? productState.dimensions.height.inches.toFixed(2)
					: '',
				width: productState.dimensions.width?.inches
					? productState.dimensions.width.inches.toFixed(2)
					: '',
				depth: productState.dimensions.depth?.inches
					? productState.dimensions.depth.inches.toFixed(2)
					: '',
				weight: productState.dimensions.weight?.ounces
					? productState.dimensions.weight.ounces.toFixed(2)
					: ''
			},
			productMSRP: productState.productMSRP ? productState.productMSRP : '',
			productGTIN: productState.productGTIN ? productState.productGTIN : '',
			launchYear: productState.launchYear ? productState.launchYear : '',
			productShelfLife: productState.productShelfLife ? productState.productShelfLife : ''
		},
		onSubmit: values => {
			onUpdateProfileProduct(values);
		},
		validationSchema: ProductDetailFormSchema
	});

	useEffect(() => {
		if (productState) {
			setID('product-details-' + productState.id);
		}
	}, [productState]);
	useEffect(() => {
		onChange(values);
		onValidationChange(isValid);
	}, [values, isValid]);

	useEffect(() => {
		handleReset(null);
	}, [productState, isEditing]);
	useEffect(() => {
		setValues({
			...productState,
			flagshipProduct: productState.flagshipProduct,
			dimensions: {
				height: productState.dimensions.height?.inches
					? productState.dimensions.height.inches.toFixed(2)
					: '',
				width: productState.dimensions.width?.inches
					? productState.dimensions.width.inches.toFixed(2)
					: '',
				depth: productState.dimensions.depth?.inches
					? productState.dimensions.depth.inches.toFixed(2)
					: '',
				weight: productState.dimensions.weight?.ounces
					? productState.dimensions.weight.ounces.toFixed(2)
					: ''
			},
			productMSRP: productState.productMSRP ? productState.productMSRP : '',
			productGTIN: productState.productGTIN ? productState.productGTIN : '',
			launchYear: productState.launchYear ? productState.launchYear : '',
			productShelfLife: productState.productShelfLife ? productState.productShelfLife : ''
		});
	}, [productState, isEditing]);
	useEffect(() => {
		if (ID === 'product-details-' + productState.id) {
			setErrors(formatErrors(errors, DEFAULT_LABELS));
		}
	}, [errors]);
	return (
		<div className='product-details-form' id={ID ? ID : ''}>
			<TitleForms
				isEditing={isEditing === ID}
				isVisible={isVisible}
				onUpdate={() => submitForm()}
				isValid={isValid}
				titleText='Product Details'
				toggleEditing={() => toggleEditing(ID)}
				toggleVisibility={toggleVisibility}
			></TitleForms>
			{ID === profileState.sectionId && profileState.loading ? (
				<LoadingBobComponent></LoadingBobComponent>
			) : (
				<>
					{isVisible &&
						(isEditing !== ID ? (
							<>
								<ContainerForms
									FormComponent1={
										<>
											{' '}
											<div>
												<LabelForms
													text="Is This your Brand's Flagship Product?
											"
													icon={<i className='ri-question-line' />}
													toolTipText={
														<>
															Your Flagship product is a best-seller or fan favorite product from
															your brand. For Newtopia Now, this product is featured in The Market.
														</>
													}
													toolTipPosition='right'
												></LabelForms>
												<CheckboxFormComponent
													checkboxType='toggle'
													name='flagshipProduct'
													value={productState.flagshipProduct}
													disabled={true}
													onClick={() => toggleEditing(ID)}
												></CheckboxFormComponent>
											</div>
											<div>
												<LabelForms
													text='Product MSRP'
													icon={<i className='ri-question-line' />}
													toolTipText={
														<>
															Manufacturer's Suggested Retail Price - also known as list price or
															recommended retail price.
														</>
													}
												></LabelForms>
												<p onClick={() => toggleEditing(ID)}>{productState.productMSRP}</p>
											</div>
											<div>
												<LabelForms
													text='Parent GTIN'
													toolTipText={
														<>
															A Global Trade Item Number is a unique and internationally recognized
															identifier for a product. When available, it will appear next to the
															barcode on your product's packaging.
														</>
													}
													icon={<i className='ri-question-line' />}
												></LabelForms>
												<p onClick={() => toggleEditing(ID)}>
													{productState.productGTIN ? productState.productGTIN : ''}
												</p>
											</div>
										</>
									}
									FormComponent2={
										<>
											{' '}
											<div>
												<LabelForms text='Product Dimensions' />
												{Object.keys(values.dimensions ?? {}).map(key => {
													const dim = productState.dimensions?.[key] ?? {};
													const ounces = dim.ounces ?? 0;
													const grams = dim.grams ?? 0;
													const inches = dim.inches ?? 0;
													const millimeters = dim.millimeters ?? 0;
													const isWeight = 'ounces' in dim;
													return (
														<div
															className='dimension-details'
															onClick={() => toggleEditing(ID)}
															key={key}
														>
															<p>{capitalizeString(key)}</p>
															<p>
																{isWeight
																	? ` ${ounces?.toFixed(2)} oz; ${grams.toFixed(2)} gr`
																	: ` ${inches?.toFixed(2)} in; ${(millimeters / 10).toFixed(
																			2
																	  )} cm`}
															</p>
														</div>
													);
												})}
											</div>
											<div>
												<LabelForms
													text='Launch Year of the Product'
													toolTipText={
														<>
															This one is important! If your product launched in the current or
															previous year, we will include it in our New to Market filter that
															retailers see.
														</>
													}
													icon={<i className='ri-question-line' />}
												></LabelForms>
												<p onClick={() => toggleEditing(ID)}>{productState.launchYear}</p>
											</div>
											<div>
												<LabelForms text='Product Shelf Life (in months)'></LabelForms>
												<p onClick={() => toggleEditing(ID)}>{productState.productShelfLife}</p>
											</div>
										</>
									}
								></ContainerForms>
							</>
						) : (
							<>
								<ContainerForms
									FormComponent1={
										<>
											{' '}
											<div>
												<LabelForms
													text="Is this your brand's Flagship Product?"
													toolTipText={
														<>
															Your Flagship product is a best-seller or fan favorite product from
															your brand. For Newtopia Now, this product is featured in The Market.
														</>
													}
													icon={<i className='ri-question-line'></i>}
												></LabelForms>
												<CheckboxFormComponent
													checkboxType='toggle'
													name='flagshipProduct'
													value={values.flagshipProduct}
													disabled={false}
													onChange={handleChange}
												></CheckboxFormComponent>
											</div>
											<div>
												<LabelForms
													text='Product MSRP'
													icon={<i className='ri-question-line' />}
													toolTipText={
														<>
															Manufacturer's Suggested Retail Price - also known as list price or
															recommended retail price.
														</>
													}
												></LabelForms>
												<TextInputComponent
													error={errors.productMSRP?.length! > 0}
													errorHelperText={<div>{errors.productMSRP}!</div>}
													placeholder='Product MSRP'
													name='productMSRP'
													onChange={handleChange}
													value={values.productMSRP!}
													textStart={'$'}
												></TextInputComponent>
											</div>
											<div>
												<LabelForms
													text='Parent GTIN'
													toolTipText={
														<>
															A Global Trade Item Number is a unique and internationally recognized
															identifier for a product. When available, it will appear next to the
															barcode on your product's packaging.
														</>
													}
													icon={<i className='ri-question-line' />}
												></LabelForms>
												<p>{values.productGTIN!}</p>
											</div>
										</>
									}
									FormComponent2={
										<>
											{' '}
											<div>
												<LabelForms text='Product Dimensions'></LabelForms>
											</div>
											{Object.keys(values.dimensions!).map(key => (
												<div key={key}>
													<TextInputComponent
														error={getIn(errors, `dimensions.${key}`) !== undefined}
														errorHelperText={<div>{getIn(errors, `dimensions.${key}`)}!</div>}
														placeholder={formatDimentionsPlaceholder(key)}
														name={`dimensions.${key}`}
														onChange={handleChange}
														value={values.dimensions![key as keyof typeof dimensionsProducts]}
														textStart={formatDimentionsPlaceholder(key)}
													></TextInputComponent>
												</div>
											))}
											<div className='launch-section'>
												<div>
													<LabelForms
														text='Launch Year of the Product'
														toolTipText={
															<>
																This one is important! If your product launched in the current or
																previous year, we will include it in our New to Market filter that
																retailers see.
															</>
														}
														icon={<i className='ri-question-line' />}
													></LabelForms>

													<TextInputComponent
														error={errors.launchYear?.length! > 0}
														errorHelperText={<div>{errors.launchYear}!</div>}
														placeholder='Launch Year of the Product'
														name='launchYear'
														onChange={handleChange}
														value={values.launchYear!}
													></TextInputComponent>
												</div>
												<div>
													<LabelForms text='Product Shelf Life (in months)'></LabelForms>
													<TextInputComponent
														error={errors.productShelfLife?.length! > 0}
														errorHelperText={<div>{errors.productShelfLife}!</div>}
														placeholder='Product Shelf Life (in months)'
														name='productShelfLife'
														onChange={handleChange}
														value={values.productShelfLife!}
													></TextInputComponent>
												</div>
											</div>
										</>
									}
									classColumn2={'custom-spacing'}
								></ContainerForms>
								<ActionsForms
									onUpdate={() => submitForm()}
									onCancel={() => {
										onCancel();
										handleReset(null);
									}}
									isValid={isValid}
									toggleEditing={() => toggleEditing(null)}
								></ActionsForms>
							</>
						))}
				</>
			)}
		</div>
	);
};

export default React.memo(ProductDetailsFormComponent);
